
import Vue from "vue";
import { mapActions } from "vuex";

import MenuTreeView from "./MenuTreeView.vue";
import LoadingCompanyHeader from "../LoadingCompanyHeader.vue";
import LoadingMenuTree from "../LoadingMenuTree.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

export default Vue.extend({
  name: "Sidebar",
  components: { MenuTreeView, LoadingCompanyHeader, LoadingMenuTree, SearchIcon },
  computed: {
    routeName() {
      return this.$route.name;
    },
    isSidebar: {
      get() {
        return this.$store.state.app.isSidebar;
      },
      set(value: boolean) {
        this.$store.dispatch("setApp", {
          ...this.$store.state.app,
          isSidebar: value,
        });
      },
    },
    company() {
      return this.$store.getters.company;
    },
    user() {
      return this.$store.getters.user;
    },
    menuItems() {
      return this.$store.getters.structure || [];
    },
    filteredMenuItems(): any[] {
      const isAdmin = (this as any).user?.role === "ADMIN";
      const query = (this as any).searchQuery.trim().toLowerCase();

      const filterNodes = (nodes: any[]): any[] => {
        return nodes.reduce((acc: any[], node: any) => {
          if (!isAdmin && node.type === "model" && !node.active) return acc;
          const nameMatch = node.name && node.name.toLowerCase().includes(query);
          if (!query || nameMatch) {
            if (node.children && node.children.length > 0) {
              acc.push({ ...node, children: filterNodes(node.children) });
            } else {
              acc.push(node);
            }
          } else if (node.children && node.children.length > 0) {
            const filteredChildren = filterNodes(node.children);
            if (filteredChildren.length > 0) {
              acc.push({ ...node, children: filteredChildren });
            }
          }
          return acc;
        }, []);
      };

      return (this as any).menuItems.map((root: any) => ({
        ...root,
        children: filterNodes(root.children || []),
      }));
    },
    searchOpenIds(): string[] {
      if (!(this as any).searchQuery.trim()) return [];
      const keys: string[] = [];
      const collect = (nodes: any[]) => {
        nodes.forEach((node: any) => {
          if (node.id !== undefined) keys.push(`${node.type}-${node.id}`);
          if (node.children?.length) collect(node.children);
        });
      };
      collect((this as any).filteredMenuItems);
      return keys;
    },
    remainingDay() {
      const dt = new Date(this.company.end_date);
      const dateToday = new Date();
      const diffDate = dt.getTime() - dateToday.getTime();
      const dfDays = diffDate / (1000 * 60 * 60 * 24);

      if (dateToday < dt) {
        return ` (${dfDays.toFixed(0)} days left)`;
      } else {
        return ``;
      }
    },
  },
  data() {
    return {
      width: 300,
      borderSize: 5,
      isLoading: true,
      searchQuery: "",
      logoDefault: require("@/assets/images/default-factory.png"),
      bypassRouteNameList: [
        "MonitoringPage",
        "CompaniesPage",
        "AdminPage",
        "ManageMemberPage",
        "OperationMonitorPage",
        "AskCenAiPage",
        "RvsCalculationPage",
      ],
    };
  },
  async created() {
    try {
      this.isLoading = true;

      const idFromRoute = this.$route.params.id;
      const companyIdFromQuery = this.$route.query.company_id;
      const isCompanyRoute = this.$route.path.startsWith("/company/");
      const isModelRoute = this.$route.path.includes("/model/");

      const bypassRouteName = this.bypassRouteNameList.includes(this.routeName);

      let companyId = null;
      // 1. Get company ID from query or route
      if (companyIdFromQuery) {
        companyId = companyIdFromQuery;
      } else if (isCompanyRoute && idFromRoute) {
        companyId = idFromRoute;
      }
      // 2. Or if from model route
      else if (isModelRoute && idFromRoute) {
        const model = await this.$store.dispatch("getModel", idFromRoute);
        companyId = model?.structureItem?.companies_id;
      }

      // 3. If still no companyId, redirect
      if (!bypassRouteName && !companyId) {
        this.$router.replace({ name: "CompaniesPage" });
        return;
      }

      // 4. Save companyId and fetch project structure data
      if (!bypassRouteName) {
        sessionStorage.setItem("company_id", companyId);
        await this.$store.dispatch("getCompany", companyId);
        const response = await this.$store.dispatch("getStructure", companyId);

        if (response?.status >= 400 && response?.status < 500) {
          this.$router.replace({ name: "CompaniesPage" });
        }
      }
    } catch (error) {
      console.error(error);
      this.$router.replace({ name: "CompaniesPage" });
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions(["getStructure", "getCompany"]),
    setBorderWidth() {
      const i = (this as any).$refs.drawer.$el.querySelector(".v-navigation-drawer__border");
      i.style.width = this.borderSize + "px";
      i.style.cursor = "ew-resize";
    },
    setEvents() {
      const minSize = this.borderSize;
      const vm = this;
      const el = (this as any).$refs.drawer.$el;
      const drawerBorder = el.querySelector(".v-navigation-drawer__border");

      const minMax = (w: number): number => (w < 300 ? 300 : w > 480 ? 480 : w);

      function resize(e: any) {
        const { clientX } = e;
        document.body.style.cursor = "ew-resize";
        el.style.width = minMax(clientX) + "px";
      }

      drawerBorder.addEventListener("mousedown", function (e: any) {
        if (e.offsetX < minSize) {
          document.addEventListener("mousemove", resize, false);
        }
      });

      document.addEventListener(
        "mouseup",
        function () {
          const size = Number(el.style.width.replace("px", "")) || 0;
          el.style.transition = "";
          vm.width = minMax(size);
          document.body.style.cursor = "";
          document.removeEventListener("mousemove", resize, false);
        },
        false
      );
    },
    convertExpiredDate(date: any) {
      if (!date) return "-";
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const dt = new Date(date);
      const day = `${dt.getDate()}`.padStart(2, "0");
      const month = `${monthNames[dt.getMonth()].substring(0, 3)}`;
      const year = `${dt.getFullYear()}`;
      return `${day} ${month} ${year}`;
    },
    checkDateExpired(date: any) {
      if (!date) return false;
      const dt = new Date(date);
      const dateToday = new Date();
      const diffDate = dt.getTime() - dateToday.getTime();
      const dfDays = diffDate / (1000 * 60 * 60 * 24);

      if (dfDays <= 30) return true;
      return false;
    },
    handleBacktoOverall() {
      const id = this.company?.id;
      if (id) this.$router.push(`/company/${id}`);
    },
    imagePathError(event: any) {
      event.target.src = this.logoDefault;
    },
  },
  mounted() {
    this.setBorderWidth();
    this.setEvents();
  },
  watch: {
    isSidebar(val) {
      if (val) {
        this.width = 300;
      }
    },
  },
});
